import { useState, useCallback } from 'react';

import { createEmail } from '../../../../../../api';
import { uploadAttachments } from '../../../utils/uploadAttachments';
import { Attachment, EmailAddress } from '../types';

const DEFAULT_ERROR_MESSAGE = 'Failed to send email';

type CreateEmailParams = {
  customerId: number;
  toEmailAddress: string;
  toName?: string;
  subject: string;
  body: string;
  htmlBody?: string;
  fromEmailAddress?: string;
  fromName?: string;
  secondaryToAddresses?: EmailAddress[];
  ccAddresses?: EmailAddress[];
  bccAddresses?: EmailAddress[];
  attachments: Attachment[];
};

type UseCreateEmailResult = {
  sendEmail: (params: CreateEmailParams) => Promise<boolean>;
  isSubmitting: boolean;
  error: string | null;
  setError: (error: string | null) => void;
};

export const useCreateEmail = (): UseCreateEmailResult => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const sendEmail = useCallback(async({
    customerId,
    toEmailAddress,
    toName,
    subject,
    body,
    htmlBody,
    fromEmailAddress,
    fromName,
    secondaryToAddresses,
    ccAddresses,
    bccAddresses,
    attachments,
  }: CreateEmailParams): Promise<boolean> => {
    setIsSubmitting(true);
    setError(null);

    try {
      const { urls } = await uploadAttachments(attachments);

      await createEmail(customerId, {
        to_email_address: toEmailAddress,
        subject,
        text_body: body,
        ...(htmlBody && { html_body: htmlBody }),
        ...(toName && { to_name: toName }),
        ...(fromEmailAddress && { from_email_address: fromEmailAddress }),
        ...(fromName && { from_name: fromName }),
        ...(secondaryToAddresses && secondaryToAddresses.length > 0 && { secondary_to_addresses: secondaryToAddresses }),
        ...(ccAddresses && ccAddresses.length > 0 && { cc_addresses: ccAddresses }),
        ...(bccAddresses && bccAddresses.length > 0 && { bcc_addresses: bccAddresses }),
        ...(urls.length > 0 && { media_urls: urls }),
      });

      return true;
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : DEFAULT_ERROR_MESSAGE;
      setError(errorMessage);
      return false;
    } finally {
      setIsSubmitting(false);
    }
  }, []);

  return { sendEmail, isSubmitting, error, setError };
};
